/**
 * The deck a book became (spec §12 (c) done condition): back to the subject, a chapter picker,
 * the StudyView for the chosen deck, the rating strip and the coverage report — verify's
 * findings in the student's words, so a gap is expected rather than alarming (design of
 * record §6). Design v2 screen 2.
 */
import type { ApiFailure } from "../api/client";
import { FailedPage, LoadingPage, transient } from "../states/Page";
import { useEffect, useState } from "react";
import type { Api, Book, CoverageSummary, DeckDoc, FindingWire } from "../api/api";
import { StudyView } from "../cards/StudyView";
import { RatingStrip } from "../feedback/RatingStrip";
import "../styles/cards.css";
import { groupFindings, label } from "./coverage";
import { markStudied, studiedChapters } from "./studied";
import { apiMessage } from "../text/errors";

type Props = {
  api: Api;
  bookId: string;
  sleep?: (ms: number) => Promise<void>;
  onBack?: (subjectId: string) => void;
  /** A chapter's pages look wrong: fix them (bug fix 1, spec 7). */
  onFixChapter?: (chapter: number) => void;
};

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function minutes(seconds: number): string {
  const m = Math.max(1, Math.ceil(seconds / 60));
  return `about ${m} ${m === 1 ? "minute" : "minutes"}`;
}

function Waiting({ book }: { book: Book }) {
  const failed = book.status === "failed";
  const p = book.progress;
  return (
    <main className="narrow">
      <p className="eyebrow">{book.filename}</p>
      <h1>{failed ? "This book needs attention" : "Still making your flashcards…"}</h1>
      {failed ? (
        <p className="muted">
          The flashcards could not be finished. Check the chapters and try again.
        </p>
      ) : (
        <>
          {p && p.chapters_done > 0 && (
            <p>
              {p.chapters_done} of {p.chapters_total} chapters done
            </p>
          )}
          {/* API_CHANGES § Copy changed: no chapter opens before the whole book is done. */}
          <p className="muted">
            {p
              ? `Cards open when the whole book is done — ${minutes(p.eta_seconds)}. You can leave and come back.`
              : "Cards open when the whole book is done. This takes a few minutes; you can leave and come back."}
          </p>
        </>
      )}
    </main>
  );
}

function CoverageReport({
  findings,
  summary,
}: {
  findings: FindingWire[];
  summary: CoverageSummary | undefined;
}) {
  const groups = groupFindings(findings);
  if (groups.length === 0) return null;
  return (
    <section className="coverage-report" aria-label="Coverage report">
      <h2>What to know about this book</h2>
      <p className="coverage-report__intro">
        From the check Carrel runs after making the cards.
        {summary && ` ${summary.complete} of ${summary.chapters_total} chapters are complete.`}
      </p>
      {groups.map((g) => (
        <div key={g.chapter ?? "book"} className="coverage-report__group">
          <h3>{g.chapter === null ? "The whole book" : `Chapter ${g.chapter}`}</h3>
          <ul>
            {g.items.map((f, i) => (
              <li key={i}>
                <span className={`coverage-report__label coverage-report__label--${f.severity}`}>
                  {label(f.severity)}
                </span>{" "}
                — {f.message}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export function StudyScreen({ api, bookId, sleep = realSleep, onBack, onFixChapter }: Props) {
  const [book, setBook] = useState<Book | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const [deck, setDeck] = useState<DeckDoc | null>(null);
  const [findings, setFindings] = useState<FindingWire[]>([]);
  const [summary, setSummary] = useState<CoverageSummary | undefined>(undefined);
  const [chosen, setChosen] = useState<number | null>(null);
  const [studied, setStudied] = useState<number[]>(() => studiedChapters(bookId));
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<ApiFailure | null>(null);
  const [run, setRun] = useState(0);

  useEffect(() => {
    let live = true;
    setFailed(null);
    void (async () => {
      for (let attempt = 0; live && attempt < 180; attempt++) {
        const b = await api.getBook(bookId);
        if (!live) return;
        if (!b.ok) return transient(b) ? setFailed(b) : setError(apiMessage(b.error));
        setBook(b.data);
        if (attempt === 0) {
          // The back link's name only; a failure leaves it saying "Back".
          void api.listSubjects().then((r) => {
            const s = r.ok ? r.data.subjects.find((x) => x.id === b.data.subject_id) : undefined;
            if (live && s) setSubject(s.name);
          });
        }
        if (b.data.status === "ready") {
          const [d, f] = await Promise.all([api.getDeck(bookId), api.getFindings(bookId)]);
          if (!live) return;
          if (d.ok) setDeck(d.data);
          else if (transient(d)) setFailed(d);
          else setError(apiMessage(d.error));
          if (f.ok) {
            setFindings(f.data.findings);
            setSummary(f.data.summary);
          }
          return;
        }
        if (b.data.status === "failed") return;
        await sleep(5000);
      }
    })();
    return () => {
      live = false;
    };
  }, [api, bookId, sleep, run]);

  if (failed) return <FailedPage failure={failed} onRetry={() => setRun((n) => n + 1)} />;
  if (error) return <p role="alert">{error}</p>;
  if (!book) return <LoadingPage />;
  if (book.status !== "ready") return <Waiting book={book} />;
  if (!deck || deck.decks.length === 0) {
    return <p className="muted">No cards came out of this book.</p>;
  }
  const decks = deck.decks;
  const index = Math.max(
    0,
    decks.findIndex((d) => d.number === chosen),
  );
  const current = decks[index]!;
  const following = decks[index + 1];
  // "of 29" only when the numbers run 1..n; a skipped chapter would make it read wrong.
  const contiguous = decks.every((d, i) => d.number === i + 1);
  const done = new Set(studied.filter((n) => decks.some((d) => d.number === n)));
  const complete = () => setStudied(markStudied(bookId, current.number));
  return (
    <main className="study-screen">
      <nav className="study-screen__nav" aria-label="Chapters">
        <button
          type="button"
          className="study-screen__back"
          onClick={() => onBack?.(book.subject_id)}
        >
          ‹ {subject ?? "Back"}
        </button>
        <label className="picker">
          <span className="picker__face" aria-hidden="true">
            Chapter {current.number}
            {contiguous && <span className="picker__of"> of {decks.length}</span>} ▾
          </span>
          <select
            className="picker__select"
            aria-label="Chapter"
            value={current.number}
            onChange={(e) => setChosen(Number(e.target.value))}
          >
            {decks.map((d) => (
              <option key={d.number} value={d.number}>
                {d.number}. {d.title} · {d.cards.length}
                {done.has(d.number) ? " ✓" : ""}
              </option>
            ))}
          </select>
        </label>
      </nav>
      {/* Keyed so a new chapter starts a fresh session rather than inheriting the last. */}
      <StudyView
        key={current.number}
        deck={current}
        onWrongPages={onFixChapter ? () => onFixChapter(current.number) : undefined}
        headingLevel={2}
        next={
          following
            ? { number: following.number, title: following.title, cards: following.cards.length }
            : null
        }
        studied={{ done: new Set([...done, current.number]).size, total: decks.length }}
        onNext={following ? () => setChosen(following.number) : undefined}
        onComplete={complete}
      />
      <RatingStrip api={api} kind="deck" refId={bookId} label="Were these cards useful?" />
      <CoverageReport findings={findings} summary={summary} />
    </main>
  );
}
